import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Brand from "@/components/Brand";

const EASE = [0.76, 0, 0.24, 1];

function isSafeNext(next) {
  if (!next || typeof next !== "string") return false;
  // Only allow same-origin absolute paths starting with a single slash.
  return next.startsWith("/") && !next.startsWith("//");
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextParam = params.get("next");
  const destination = isSafeNext(nextParam) ? nextParam : "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [appCount, setAppCount] = useState(null);
  useEffect(() => { api.get("/portal-count").then(({data})=>setAppCount(data.count)).catch(()=>setAppCount(null)); }, []);

  if (user && user !== false && user !== null) {
    return <Navigate to={destination} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (result.success) {
      // Full navigation ensures cross-module cookies + Partners AuthProvider
      // re-initialize when we land on /partners.
      if (destination.startsWith("/partners")) {
        window.location.href = destination;
      } else {
        navigate(destination, { replace: true });
      }
    } else {
      setError(result.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Top nav */}
      <header className="border-b border-border">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <Brand eyebrow="UNIFIED ECOSYSTEM" />
          <span className="font-mono-meta text-muted-foreground">
            Stabicon · Lyrus
          </span>
        </div>
      </header>

      {/* Hero */}
      <main className="relative flex-1 overflow-hidden">
        <div className="hero-watermark" aria-hidden="true" />

        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: hero copy */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="lg:col-span-7"
          >
            <div className="font-mono-meta text-foreground mb-8">
              Partners · Formulation Master · Dossier Assistant · Clinical Intelligence — One Sign-In
            </div>

            <h1
              className="font-serif-display text-[44px] md:text-[64px] lg:text-[72px] mb-8"
              data-testid="login-hero-title"
            >
              Every Chandan application, <br />
              on <em className="italic">a single desk</em>.
            </h1>

            <p className="text-foreground/80 text-base md:text-[17px] leading-[1.6] max-w-xl mb-12">
              The unified control surface for the Chandan ecosystem. Access your
              workspaces, search across them, and watch cross-app data
              stream into a single ledger — with one secure account.
            </p>

            {/* Compact login form */}
            <form onSubmit={onSubmit} className="max-w-md" data-testid="login-form">
              <div className="mb-6">
                <label htmlFor="login-field-98" className="font-mono-meta text-muted-foreground block mb-2">
                  Email
                </label>
                <input id="login-field-98"
                  type="email"
                  autoComplete="email"
                  required
                  className="input"
                  placeholder="you@chandan.io"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                />
              </div>
              <div className="mb-8">
                <label htmlFor="login-field-113" className="font-mono-meta text-muted-foreground block mb-2">
                  Password
                </label>
                <input id="login-field-113"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                />
              </div>

              {error && (
                <div
                  className="mb-6 border border-destructive/40 bg-destructive/5 text-destructive font-mono-meta px-4 py-3"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <div className="flex items-center gap-4">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                  data-testid="login-submit-button"
                >
                  {submitting ? "Signing in" : "Sign In"}
                  <ArrowRight size={14} />
                </button>
                <span className="font-mono-meta text-muted-foreground">
                  One secure account
                </span>
              </div>
            </form>
          </motion.section>

          {/* Right: 2×2 stat block */}
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
            className="lg:col-span-5"
            data-testid="login-stats"
          >
            <div className="card grid grid-cols-2 divide-x divide-y-0 divide-border">
              {[
                { label: "Applications", value: appCount ?? "—" },
                { label: "Shared Ledger", value: "1" },
                { label: "Owning companies", value: "2" },
                { label: "Sign-In", value: "SSO", meta: "One account, all apps" },
              ].map((m, i) => (
                <div
                  key={m.label}
                  className={`p-8 ${i > 1 ? "border-t border-border" : ""}`}
                  data-testid={`login-stat-${i}`}
                >
                  <div className="font-sans text-[44px] leading-none tabular text-foreground mb-3">
                    {m.value}
                  </div>
                  <div className="font-mono-meta text-muted-foreground">
                    {m.label}
                  </div>
                  {m.meta && (
                    <div className="text-xs text-muted-foreground mt-2">
                      {m.meta}
                    </div>
                  )}
                </div>
              ))}
            </div>

          </motion.aside>
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-14 flex items-center justify-between font-mono-meta text-muted-foreground">
          <span>© {new Date().getFullYear()} Chandan</span>
          <span>Unified Ecosystem</span>
        </div>
      </footer>
    </div>
  );
}
